import React, { useState, useMemo, useEffect } from "react";
import {
  Box,
  Paper,
  Typography,
  TextField,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  IconButton,
  Radio,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Divider,
  FormControlLabel,
  Switch,
  Card,
  Grid,
} from "@mui/material";
import { styled } from "@mui/material/styles";
import CloseIcon from "@mui/icons-material/Close";
import SmsOutlinedIcon from "@mui/icons-material/SmsOutlined";
import ArrowBackIosNewIcon from "@mui/icons-material/ArrowBackIosNew";
import BatteryFullIcon from "@mui/icons-material/BatteryFull";
import WifiIcon from "@mui/icons-material/Wifi";
import NetworkCellIcon from "@mui/icons-material/NetworkCell";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";

import { useFetchPrincipalEntitiesdataQuery } from "../store/services/principleApi";
import { useGetTemplateByPrincipalIdQuery } from "../store/services/templateApi";
import { useFetchHeadersByPrincipalIdQuery } from "../store/services/headerApi ";
import {
  useCheckMpinStatusQuery,
  useVerifyMpinStatusMutation,
} from "../store/services/userApi";
import { maskNumber } from "../component/common/Encryption";

// ─── Field styling (same as RCS component) ────────────────────────────────────
const fieldSx = {
  height: 35,
  borderRadius: "8px",
  background: "rgba(255,255,255,0.75)",
  "& .MuiOutlinedInput-notchedOutline": {
    borderColor: "#CBD5E1",
    borderWidth: "1px",
  },
  "&:hover .MuiOutlinedInput-notchedOutline": {
    borderColor: "#94A3B8",
  },
  "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
    borderColor: "#7c3aed",
    borderWidth: "2px",
  },
  "&.Mui-disabled .MuiOutlinedInput-notchedOutline": {
    borderColor: "#E2E8F0",
  },
};

const textFieldSx = {
  "& .MuiOutlinedInput-root": {
    ...fieldSx,
  },
};

const selectSx = {
  "& .MuiOutlinedInput-root": {
    ...fieldSx,
    "& .MuiSelect-select": {
      padding: "0 14px",
      fontSize: "0.8rem",
      display: "flex",
      alignItems: "center",
      height: "100%",
      boxSizing: "border-box",
      minHeight: "unset",
    },
    "& .MuiSelect-icon": {
      right: 8,
      fontSize: "1.15rem",
    },
  },
};

// Small helper — consistent bordered section wrapper
const SectionBox = ({ title, children, sx }) => (
  <Box
    sx={{
      p: 1.5,
      mb: 2,
      borderRadius: "10px",
      border: "1px solid #E2E8F0",
      backgroundColor: "rgba(255,255,255,0.55)",
      ...sx,
    }}
  >
    {title && (
      <Typography
        variant="subtitle2"
        sx={{ fontWeight: 600, mb: 1.25, color: "#0F172A" }}
      >
        {title}
      </Typography>
    )}
    {children}
  </Box>
);

// ─── Phone preview ────────────────────────────────────────────────────────────
const PhonePreview = styled(Card)(() => ({
  width: "260px",
  height: "460px",
  margin: "0 auto",
  border: "1px solid #E2E8F0",
  boxShadow: "0 8px 24px rgba(0, 0, 0, 0.08)",
  borderRadius: "28px",
  overflow: "hidden",
  display: "flex",
  flexDirection: "column",
  background: "#0F172A",
  padding: "6px",
}));

// ─── Template variable helpers (SMS uses {#var#} syntax) ─────────────────────
const extractTemplateVariables = (...texts) => {
  const regex = /\{#\s*([^#{}]+?)\s*#\}/g;
  const counts = new Map();
  const out = [];

  texts.filter(Boolean).forEach((text) => {
    let match;
    while ((match = regex.exec(text)) !== null) {
      const key = match[1].trim();
      if (!key) continue;

      const occurrence = counts.get(key) || 0;
      counts.set(key, occurrence + 1);
      const variableKey = occurrence === 0 ? key : `${key}_${occurrence + 1}`;
      out.push(variableKey);
    }
  });

  return out;
};

const buildInitialVariableValues = (keys) => {
  const obj = {};
  keys.forEach((k) => {
    obj[k] = "";
  });
  return obj;
};

const getVariableDisplayLabel = (key) => {
  const match = key.match(/^(.*)_(\d+)$/);
  if (!match) return `{#${key}#}`;
  return `{#${match[1]}#} (${match[2]})`;
};

const substituteVariables = (text, variables) => {
  if (!text) return text;

  const occurrences = {};
  const pattern = /\{#\s*([^#{}]+?)\s*#\}/g;

  return text.replace(pattern, (fullMatch, rawKey) => {
    const key = rawKey.trim();
    const occurrence = occurrences[key] = (occurrences[key] || 0) + 1;
    const variableKey = occurrence === 1 ? key : `${key}_${occurrence}`;
    const value = variables?.[variableKey] ?? variables?.[key];

    if (value !== undefined && value !== null && value !== "") {
      return String(value);
    }

    return fullMatch;
  });
};

// ─── SMS length / count / encoding helpers ───────────────────────────────────
const GSM7_BASIC =
  "@£$¥èéùìòÇ\nØø\rÅåΔ_ΦΓΛΩΠΨΣΘΞ\u001BÆæßÉ !\"#¤%&'()*+,-./0123456789:;<=>?¡ABCDEFGHIJKLMNOPQRSTUVWXYZÄÖÑÜ§¿abcdefghijklmnopqrstuvwxyzäöñüà";
const GSM7_EXT = "^{}\\[~]|€";

const isGsm7 = (text) => {
  for (const ch of text) {
    if (GSM7_BASIC.includes(ch)) continue;
    if (GSM7_EXT.includes(ch)) continue;
    return false;
  }
  return true;
};

const getGsm7Length = (text) => {
  let len = 0;
  for (const ch of text) {
    len += GSM7_EXT.includes(ch) ? 2 : 1;
  }
  return len;
};

const getSmsStats = (text) => {
  if (!text) {
    return {
      count: 0,
      encoding: "GSM-7",
      length: 0,
      singleLimit: 160,
      multiLimit: 153,
    };
  }

  if (isGsm7(text)) {
    const length = getGsm7Length(text);
    const singleLimit = 160;
    const multiLimit = 153;
    const count = length <= singleLimit ? 1 : Math.ceil(length / multiLimit);
    return { count, encoding: "GSM-7", length, singleLimit, multiLimit };
  }

  const length = [...text].length;
  const singleLimit = 70;
  const multiLimit = 67;
  const count = length <= singleLimit ? 1 : Math.ceil(length / multiLimit);
  return { count, encoding: "Unicode", length, singleLimit, multiLimit };
};

const OutboundSmsConfig = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const [searchParams] = useSearchParams();
  const isEditMode = searchParams.get("edit") === "true";
  const nodeId = searchParams.get("nodeId");

  // State
  const [entityId, setEntityId] = useState("");
  const [senderId, setSenderId] = useState("");
  const [filterDatatill_days, setFilterDatatill_days] = useState(0);
  const [templateName, setTemplateName] = useState("");
  const [message, setMessage] = useState("");
  const [templatePanelOpen, setTemplatePanelOpen] = useState(false);
  const [templateSearch, setTemplateSearch] = useState("");
  const [selectedTemplateId, setSelectedTemplateId] = useState(null);
  const [selectedTraiTemplateId, setSelectedTraiTemplateId] = useState(null);
  const [enableFilterTill, setEnableFilterTill] = useState(false);
  const [currentTime, setCurrentTime] = useState("");
  const [templateVariables, setTemplateVariables] = useState({});

  // Data fetching
  const { data: entitiesResp, isLoading: isEntitiesLoading } =
    useFetchPrincipalEntitiesdataQuery();
  const entities = entitiesResp?.data ?? [];

  const { data: headersResp, isLoading: isHeadersLoading } =
    useFetchHeadersByPrincipalIdQuery(entityId, {
      skip: !entityId,
    });
  const headers = headersResp?.data ?? [];

  const { data: templatesResp, isLoading: isTplLoading } =
    useGetTemplateByPrincipalIdQuery(entityId, {
      skip: !entityId || !templatePanelOpen,
    });
  const templates = templatesResp?.data ?? [];

  const { data: checkmpin } = useCheckMpinStatusQuery();
  const [verifyMpin] = useVerifyMpinStatusMutation();

  // Clock
  useEffect(() => {
    const updateTime = () => {
      const now = new Date();
      const istTime = now.toLocaleTimeString("en-IN", {
        timeZone: "Asia/Kolkata",
        hour: "2-digit",
        minute: "2-digit",
        hour12: false,
      });
      setCurrentTime(istTime);
    };
    updateTime();
    const interval = setInterval(updateTime, 60000);
    return () => clearInterval(interval);
  }, []);

  // Load saved config if editing
  useEffect(() => {
    if (!nodeId) return;
    try {
      const localStorageKey = isEditMode
        ? `workflow-edit-graph-${id}`
        : `workflow-graph-${id}`;
      const stored = localStorage.getItem(localStorageKey);
      if (!stored) return;
      const parsed = JSON.parse(stored);
      const smsNode = parsed.nodes?.find((n) => n.id === nodeId);
      const cfg = smsNode?.data?.smsConfig;
      if (!cfg) return;
      setEntityId(cfg.principalentityId || cfg.entityId || cfg.peId || "");
      setSenderId(cfg.senderId || "");
      setTemplateName(cfg.templateName || "");
      setMessage(cfg.message || cfg.template_text || "");
      setSelectedTemplateId(cfg.templateId || null);
      setSelectedTraiTemplateId(cfg.traiTemplateId || null);
      setFilterDatatill_days(cfg.filterDatatill_days ?? 0);

      if (
        cfg.template_variables &&
        typeof cfg.template_variables === "object"
      ) {
        setTemplateVariables(cfg.template_variables);
      } else {
        const keys = extractTemplateVariables(
          cfg.message || cfg.template_text || "",
        );
        setTemplateVariables(buildInitialVariableValues(keys));
      }
    } catch (err) {
      console.error("Failed to load SMS config from workflow graph", err);
    }
  }, [isEditMode, id, nodeId]);

  // Filter templates based on search
  const filteredTemplates = useMemo(() => {
    const q = templateSearch.toLowerCase();
    return templates
      .filter((t) => t.is_active)
      .filter(
        (t) =>
          t.template_name.toLowerCase().includes(q) ||
          t.template_text.toLowerCase().includes(q),
      );
  }, [templates, templateSearch]);

  // Substituted message — used for preview and character / SMS count
  const renderedMessage = substituteVariables(message, templateVariables);
  const smsStats = useMemo(
    () => getSmsStats(renderedMessage),
    [renderedMessage],
  );

  const handleEntityChange = (value) => {
    setEntityId(value);
    setSenderId("");
    setTemplateName("");
    setMessage("");
    setSelectedTemplateId(null);
    setSelectedTraiTemplateId(null);
    setTemplateVariables({});
  };

  const handleTemplateRowClick = (tpl) => {
    setSelectedTemplateId(tpl.template_id);
    setSelectedTraiTemplateId(tpl.trai_template_id);
    setTemplateName(tpl.template_name);
    setMessage(tpl.template_text);
    const keys = extractTemplateVariables(tpl.template_text);
    setTemplateVariables(buildInitialVariableValues(keys));
    setTemplatePanelOpen(false);
  };

  const handleVariableChange = (key, value) => {
    setTemplateVariables((prev) => ({ ...prev, [key]: value }));
  };

  const handleSaveToWorkflow = () => {
    try {
      const localStorageKey = isEditMode
        ? `workflow-edit-graph-${id}`
        : `workflow-graph-${id}`;
      const stored = localStorage.getItem(localStorageKey);
      if (!stored) {
        console.error(
          "No graph found in localStorage for key",
          localStorageKey,
        );
        return;
      }
      const parsed = JSON.parse(stored);
      const oldNodes = parsed.nodes || [];
      const oldEdges = parsed.edges || [];

      const selectedEntity = entities.find(
        (e) => e.principalentity_id === entityId,
      );
      const entityPeId = selectedEntity?.pe_id || null;

      const updatedNodes = oldNodes.map((n) => {
        if (n.id !== nodeId) return n;
        return {
          ...n,
          data: {
            ...n.data,
            templateName,
            smsConfig: {
              principalentityId: entityId,
              peId: entityPeId,
              senderId,
              templateId: selectedTemplateId,
              templateName,
              message,
              filterDatatill_days,
              template_variables: templateVariables,
            },
          },
        };
      });

      localStorage.setItem(
        localStorageKey,
        JSON.stringify({ nodes: updatedNodes, edges: oldEdges }),
      );
    } catch (err) {
      console.error("Failed to update workflow graph with template", err);
    }

    navigate(isEditMode ? `/workflow/edit/${id}` : `/workflow/${id}`);
  };

  const variableKeys = Object.keys(templateVariables);

  return (
    <Box
      sx={{
        height: "100vh",
        bgcolor: "#f0f2f8",
        display: "flex",
        flexDirection: "column",
        fontSize: "0.8rem",
      }}
    >
      {/* ─── Top bar ─── */}
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 1,
          px: 2,
          py: 1,
          bgcolor: "rgba(255,255,255,0.8)",
          borderBottom: "1px solid rgba(0,0,0,0.08)",
          flexShrink: 0,
        }}
      >
        <IconButton
          size="small"
          onClick={() =>
            navigate(isEditMode ? `/workflow/edit/${id}` : `/workflow/${id}`)
          }
        >
          <ArrowBackIosNewIcon sx={{ fontSize: 15, color: "black" }} />
        </IconButton>
        <SmsOutlinedIcon sx={{ fontSize: 18, color: "#ff4fa1" }} />
        <Typography sx={{ fontSize: "0.88rem", fontWeight: 600 }}>
          Outbound SMS{" "}
          {isEditMode && (
            <span style={{ fontWeight: 400, color: "#888" }}>(Edit)</span>
          )}
        </Typography>
      </Box>

      {/* ─── Body ─── */}
      <Box
        sx={{
          flex: 1,
          p: 2,
          display: "flex",
          gap: 2,
          overflow: "hidden",
          alignItems: "flex-start",
        }}
      >
        {/* LEFT: form */}
        <Paper
          elevation={0}
          sx={{
            flex: 2,
            p: 2,
            borderRadius: 2,
            bgcolor: "rgba(255,255,255,0.5)",
            backdropFilter: "blur(8px)",
            display: "flex",
            flexDirection: "column",
            gap: 2,
            height: "100%",
            overflowY: "auto",
          }}
        >
          {/* Section 1: Sender Configuration */}
          <SectionBox title="Sender Configuration">
            <Box sx={{ display: "flex", gap: 1.5, mb: 1.2 }}>
              <FormControl fullWidth size="small" sx={selectSx}>
                <InputLabel>Entity ID</InputLabel>
                <Select
                  value={entityId}
                  label="Entity ID"
                  onChange={(e) => handleEntityChange(e.target.value)}
                >
                  {isEntitiesLoading && (
                    <MenuItem disabled>Loading...</MenuItem>
                  )}
                  {!isEntitiesLoading && entities.length === 0 && (
                    <MenuItem disabled>No entities found</MenuItem>
                  )}
                  {entities.map((e) => (
                    <MenuItem
                      key={e.principalentity_id}
                      value={e.principalentity_id}
                    >
                      {maskNumber(e.pe_id)}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              <FormControl
                fullWidth
                size="small"
                disabled={!entityId}
                sx={selectSx}
              >
                <InputLabel>Sender ID</InputLabel>
                <Select
                  value={senderId}
                  label="Sender ID"
                  onChange={(e) => setSenderId(e.target.value)}
                >
                  {!entityId && (
                    <MenuItem disabled>Select entity first</MenuItem>
                  )}
                  {entityId && isHeadersLoading && (
                    <MenuItem disabled>Loading...</MenuItem>
                  )}
                  {entityId && !isHeadersLoading && headers.length === 0 && (
                    <MenuItem disabled>No headers found</MenuItem>
                  )}
                  {headers.map((h) => (
                    <MenuItem key={h.header_id} value={h.header_name}>
                      {h.header_name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Box>

            {/* Filter From LC */}
            {checkmpin?.data?.allow7_day_filter && (
              <Box>
                <FormControlLabel
                  control={
                    <Switch
                      size="small"
                      checked={enableFilterTill}
                      onChange={(e) => {
                        setEnableFilterTill(e.target.checked);
                        if (!e.target.checked) setFilterDatatill_days(0);
                      }}
                    />
                  }
                  label="Filter From LC"
                  sx={{
                    m: 0,
                    "& .MuiFormControlLabel-label": { fontSize: "0.75rem" },
                  }}
                />
                {enableFilterTill && (
                  <FormControl
                    size="small"
                    sx={{ mt: 0.8, width: 150, ...selectSx }}
                  >
                    <InputLabel>Filter Till</InputLabel>
                    <Select
                      value={filterDatatill_days}
                      label="Filter Till"
                      onChange={(e) => setFilterDatatill_days(e.target.value)}
                      MenuProps={{ PaperProps: { sx: { maxHeight: 224 } } }}
                    >
                      <MenuItem value={1}>Today</MenuItem>
                      <MenuItem value={2}>Yesterday</MenuItem>
                      {Array.from({ length: 28 }, (_, i) => (
                        <MenuItem key={i + 3} value={i + 3}>
                          Last {i + 3} days
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                )}
              </Box>
            )}
          </SectionBox>

          {/* Section 2: Select Template */}
          <SectionBox title="Select Template">
            <Box sx={{ display: "flex", gap: 1, mb: 1 }}>
              <TextField
                size="small"
                placeholder="Template Name"
                value={templateName}
                fullWidth
                InputProps={{ readOnly: true }}
                sx={textFieldSx}
              />
              <Button
                variant="outlined"
                onClick={() => setTemplatePanelOpen(true)}
                disabled={!entityId}
                sx={{
                  textTransform: "none",
                  fontSize: "0.73rem",
                  px: 1.5,
                  py: 0,
                  minWidth: 0,
                  whiteSpace: "nowrap",
                  height: 35,
                  flexShrink: 0,
                }}
              >
                Choose
              </Button>
            </Box>

            <Typography
              sx={{ fontSize: "0.67rem", color: "text.secondary", mb: 0.6 }}
            >
              Right click on variables (Vn) or within the text area for
              personalisation &amp; short URL.
            </Typography>
          </SectionBox>

          {/* Section 3: Template Variables — only when {#var#} exist */}
          {variableKeys.length > 0 && (
            <SectionBox title="Template Variables">
              <Typography
                sx={{ fontSize: "0.67rem", color: "text.secondary", mb: 1 }}
              >
                Fill values for the {"{#variable#}"} placeholders in the
                template.
              </Typography>

              <Grid container spacing={1.5}>
                {variableKeys.map((key) => (
                  <Grid item xs={4} key={key}>
                    <TextField
                      label={getVariableDisplayLabel(key)}
                      value={templateVariables[key] ?? ""}
                      onChange={(e) =>
                        handleVariableChange(key, e.target.value)
                      }
                      size="small"
                      fullWidth
                      sx={textFieldSx}
                    />
                  </Grid>
                ))}
              </Grid>
            </SectionBox>
          )}
        </Paper>

        {/* MIDDLE: phone preview */}
        <Paper
          elevation={0}
          sx={{
            flex: 1.2,
            p: 1.5,
            borderRadius: 2,
            bgcolor: "rgba(255,255,255,0.5)",
            backdropFilter: "blur(8px)",
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            height: "100%",
            flexShrink: 0,
          }}
        >
          <PhonePreview>
            <Box
              sx={{
                background: "#FFFFFF",
                borderRadius: "22px",
                height: "100%",
                display: "flex",
                flexDirection: "column",
                overflow: "hidden",
              }}
            >
              {/* Status bar */}
              <Box
                sx={{
                  width: "100%",
                  height: "28px",
                  px: 1.5,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  background: "#111827",
                  color: "#E5E7EB",
                  flexShrink: 0,
                }}
              >
                <Typography fontSize="0.65rem" opacity={0.8}>
                  {currentTime}
                </Typography>
                <Box sx={{ display: "flex", gap: 0.5, alignItems: "center" }}>
                  <WifiIcon sx={{ fontSize: "12px", opacity: 0.8 }} />
                  <NetworkCellIcon sx={{ fontSize: "12px", opacity: 0.8 }} />
                  <BatteryFullIcon sx={{ fontSize: "12px", opacity: 0.8 }} />
                </Box>
              </Box>

              {/* Chat header */}
              <Box
                sx={{
                  width: "100%",
                  height: "48px",
                  px: 1.5,
                  display: "flex",
                  alignItems: "center",
                  borderBottom: "1px solid #E2E8F0",
                  background: "#F1F5F9",
                  flexShrink: 0,
                }}
              >
                <IconButton size="small" sx={{ p: 0.5, mr: 1 }}>
                  <ArrowBackIosNewIcon sx={{ fontSize: 14 }} />
                </IconButton>
                <Typography variant="caption" sx={{ fontWeight: 600 }}>
                  {senderId || "Sender ID"}
                </Typography>
              </Box>

              {/* Message area */}
              <Box
                sx={{
                  flex: 1,
                  overflow: "hidden",
                  display: "flex",
                  flexDirection: "column",
                  minHeight: 0,
                }}
              >
                <Box
                  sx={{
                    flex: 1,
                    p: 1.5,
                    backgroundColor: "#F5F5F5",
                    overflowY: "auto",
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "flex-start",
                  }}
                >
                  {renderedMessage ? (
                    <Box
                      sx={{
                        maxWidth: "85%",
                        borderRadius: "2px",
                        bgcolor: "#ffffff",
                        px: 1.5,
                        py: 0.8,
                        boxShadow: "0 1px 2px rgba(0,0,0,0.2)",
                        border: "1px solid #E5E7EB",
                      }}
                    >
                      <Typography
                        sx={{
                          fontSize: "0.72rem",
                          color: "text.primary",
                          whiteSpace: "pre-wrap",
                          wordBreak: "break-word",
                          lineHeight: 1.4,
                        }}
                      >
                        {renderedMessage}
                      </Typography>
                    </Box>
                  ) : (
                    <Box
                      sx={{
                        width: "100%",
                        display: "flex",
                        justifyContent: "center",
                        alignItems: "center",
                        height: "100%",
                      }}
                    >
                      <Typography
                        sx={{
                          fontSize: "0.68rem",
                          color: "text.disabled",
                          textAlign: "center",
                        }}
                      >
                        Your message preview will display here.
                      </Typography>
                    </Box>
                  )}
                </Box>

                {/* Footer with char count, SMS count, encoding */}
                <Box
                  sx={{
                    px: 1.2,
                    py: 0.6,
                    bgcolor: "#ffffff",
                    borderTop: "1px solid #E0E3F0",
                    display: "flex",
                    flexDirection: "column",
                    gap: 0.2,
                  }}
                >
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                    }}
                  >
                    <Typography
                      sx={{ fontSize: "0.65rem", color: "text.secondary" }}
                    >
                      Length
                    </Typography>
                    <Typography sx={{ fontSize: "0.65rem", fontWeight: 600 }}>
                      {smsStats.length} characters
                    </Typography>
                  </Box>
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                    }}
                  >
                    <Typography
                      sx={{ fontSize: "0.65rem", color: "text.secondary" }}
                    >
                      SMS Count
                    </Typography>
                    <Typography
                      sx={{
                        fontSize: "0.65rem",
                        fontWeight: 600,
                        color: smsStats.count > 1 ? "#e65100" : "#2e7d32",
                      }}
                    >
                      {smsStats.count}{" "}
                      {smsStats.count === 1 ? "SMS" : "SMS (multi-part)"}
                    </Typography>
                  </Box>
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                    }}
                  >
                    <Typography
                      sx={{ fontSize: "0.65rem", color: "text.secondary" }}
                    >
                      Encoding
                    </Typography>
                    <Typography
                      sx={{ fontSize: "0.65rem", color: "text.secondary" }}
                    >
                      {smsStats.encoding}
                    </Typography>
                  </Box>
                </Box>
              </Box>
            </Box>
          </PhonePreview>
        </Paper>

        {/* RIGHT: template panel */}
        {templatePanelOpen && (
          <Paper
            elevation={0}
            sx={{
              width: 300,
              borderRadius: 2,
              bgcolor: "rgba(255,255,255,0.5)",
              backdropFilter: "blur(8px)",
              display: "flex",
              flexDirection: "column",
              height: "100%",
              overflow: "hidden",
              flexShrink: 0,
            }}
          >
            <Box
              sx={{
                px: 1.8,
                py: 1,
                borderBottom: "1px solid rgba(0,0,0,0.08)",
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                flexShrink: 0,
              }}
            >
              <Typography sx={{ fontSize: "0.8rem", fontWeight: 600 }}>
                Insert Template
              </Typography>
              <IconButton
                size="small"
                onClick={() => setTemplatePanelOpen(false)}
              >
                <CloseIcon sx={{ fontSize: 15, color: "red" }} />
              </IconButton>
            </Box>

            <Box sx={{ px: 1.5, py: 0.8, flexShrink: 0 }}>
              <TextField
                size="small"
                placeholder="Search templates…"
                fullWidth
                value={templateSearch}
                onChange={(e) => setTemplateSearch(e.target.value)}
                sx={textFieldSx}
                InputProps={{
                  sx: { fontSize: "0.75rem", height: 32 },
                }}
              />
            </Box>

            <Divider sx={{ borderColor: "rgba(0,0,0,0.07)" }} />

            <Box sx={{ flex: 1, overflowY: "auto", minHeight: 0 }}>
              {isTplLoading ? (
                <Typography
                  sx={{
                    fontSize: "0.75rem",
                    color: "text.secondary",
                    textAlign: "center",
                    py: 2,
                  }}
                >
                  Loading templates…
                </Typography>
              ) : (
                <Table size="small" stickyHeader>
                  <TableHead>
                    <TableRow>
                      <TableCell
                        sx={{
                          fontSize: "0.68rem",
                          py: 0.6,
                          bgcolor: "rgba(245,247,255,0.9)",
                        }}
                      />
                      <TableCell
                        sx={{
                          fontSize: "0.68rem",
                          py: 0.6,
                          bgcolor: "rgba(245,247,255,0.9)",
                        }}
                      >
                        Name
                      </TableCell>
                      <TableCell
                        sx={{
                          fontSize: "0.68rem",
                          py: 0.6,
                          bgcolor: "rgba(245,247,255,0.9)",
                        }}
                      >
                        Content
                      </TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {filteredTemplates.map((tpl) => (
                      <TableRow
                        key={tpl.template_id}
                        hover
                        onClick={() => handleTemplateRowClick(tpl)}
                        sx={{ cursor: "pointer" }}
                      >
                        <TableCell sx={{ py: 0.4, pl: 1 }}>
                          <Radio
                            size="small"
                            checked={selectedTemplateId === tpl.template_id}
                            sx={{ p: 0 }}
                          />
                        </TableCell>
                        <TableCell sx={{ fontSize: "0.73rem", py: 0.5 }}>
                          {tpl.template_name}
                        </TableCell>
                        <TableCell
                          sx={{
                            fontSize: "0.7rem",
                            py: 0.5,
                            maxWidth: 120,
                            whiteSpace: "nowrap",
                            textOverflow: "ellipsis",
                            overflow: "hidden",
                            color: "text.secondary",
                          }}
                        >
                          {tpl.template_text}
                        </TableCell>
                      </TableRow>
                    ))}
                    {!isTplLoading && filteredTemplates.length === 0 && (
                      <TableRow>
                        <TableCell colSpan={3}>
                          <Typography
                            sx={{
                              fontSize: "0.73rem",
                              color: "text.secondary",
                              textAlign: "center",
                              py: 1,
                            }}
                          >
                            No templates found.
                          </Typography>
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              )}
            </Box>
          </Paper>
        )}
      </Box>

      {/* Footer */}
      <Box
        sx={{
          px: 2,
          py: 1,
          bgcolor: "rgba(255,255,255,0.8)",
          borderTop: "1px solid rgba(0,0,0,0.08)",
          display: "flex",
          justifyContent: "flex-end",
          gap: 1,
          flexShrink: 0,
        }}
      >
        <Button
          variant="outlined"
          sx={{ textTransform: "none", fontSize: "0.75rem", py: 0.4, px: 2 }}
          onClick={() =>
            navigate(isEditMode ? `/workflow/edit/${id}` : `/workflow/${id}`)
          }
        >
          Cancel
        </Button>
        <Button
          variant="contained"
          sx={{ textTransform: "none", fontSize: "0.75rem", py: 0.4, px: 2 }}
          disabled={!entityId || !senderId || !selectedTemplateId}
          onClick={handleSaveToWorkflow}
        >
          Save
        </Button>
      </Box>
    </Box>
  );
};

export default OutboundSmsConfig;